import { graphql } from 'app/generated/gql'

/**
 * The booked half of the week.
 *
 * Through `gqlQuery` with a document rather than `gqlFetchList`, because that
 * helper turns a failed query into `{ hits: [], total: 0 }`. A week that cannot
 * load its appointments would then draw as a week with nothing booked, which is
 * the one thing this surface must never do.
 *
 * The selection is what the grid reads and what a block's tooltip says. `date`
 * and `time` are the position, `duration` the height, `status` and `pmsStatus`
 * together decide whether the chair is occupied at all, and `description` is
 * what the practice typed. There is no `operatoryId` on the model to ask for.
 */
export const SCHEDULE_APPOINTMENTS_QUERY = graphql(`
  query ScheduleWeekAppointments($search: String, $pagination: PaginationInput, $sort: SortInput) {
    appointments(search: $search, pagination: $pagination, sort: $sort) {
      hits {
        id
        locationId
        patientId
        providerId
        date
        time
        duration
        status
        pmsStatus
        type
        description
        note
        amount
        procedureCodes
      }
      total
    }
  }
`)

/**
 * The people who could fill an opening.
 *
 * Enough to rank them and draw a row, and no more. The one who gets picked is
 * fetched whole by id before the booking dialog opens, which needs the full
 * record: reading three hundred complete contacts to render twelve names would
 * be most of a megabyte for a list.
 */
export const SCHEDULE_CANDIDATE_CONTACTS_QUERY = graphql(`
  query ScheduleSlotCandidates($search: String, $pagination: PaginationInput, $sort: SortInput) {
    contacts(search: $search, pagination: $pagination, sort: $sort) {
      hits {
        id
        name
        cellPhone
        locationId
        lastHygieneDate
        patientIds
      }
      total
    }
  }
`)

/**
 * The location's chairs.
 *
 * Already a synced collection, and the names are the practice's own ("Dr.
 * Hamblin (Major)", "Carly"). The appointment record does not point at one yet,
 * so the week cannot draw them; it reads them so that the day the field lands,
 * and the day a practice can say which of them are real rooms, the names are
 * here to use.
 */
export const SCHEDULE_OPERATORIES_QUERY = graphql(`
  query ScheduleWeekOperatories($locationId: ID!) {
    operatories(locationId: $locationId) {
      id
      name
      remote_id
      display_name
      type
      locationId
    }
  }
`)

/**
 * Each room's working hours, straight from the practice management system.
 *
 * `blocks` is when a room is staffed, and there can be several: a chair that
 * works nine to one and two to four has lunch in the middle, which is not time
 * anybody can be booked into. `notes` carries the practice's own blockouts.
 *
 * A date with no record for a room means the room is not working that day. This
 * is what the board subtracts booked work from, and it is the practice's own
 * schedule rather than Trackable's availability configuration.
 */
/*
 * Typed with the generated graphql() tag so codegen validates this operation.
 */
export const SCHEDULE_ROOM_SCHEDULES_QUERY = graphql(`
  query ScheduleRoomSchedules($search: String, $pagination: PaginationInput) {
    resourceSchedules(search: $search, pagination: $pagination) {
      hits {
        id
        name
        type
        date
        blocks {
          startTime
          endTime
        }
        notes {
          startTime
          endTime
          note
        }
      }
      total
    }
  }
`)

/**
 * Which practice's rooms the hours belong to.
 *
 * `ResourceSchedule` carries no location. Its `kollaConsumerId` is the sync
 * that wrote it, and room names are the PMS's own (`resources/operatory_1`), so
 * two practices in one workspace both have a room one. Casco Bay Smiles and 207
 * Dental share the workspace "Dr. Rob McVety": read without this, the board
 * kept whichever practice's record for a room landed last and drew one
 * practice's lunch as the other's open chair.
 *
 * Typed with the generated graphql() tag so codegen validates this operation.
 */
export const SCHEDULE_DAY_LOCATION_QUERY = graphql(`
  query ScheduleDayLocation($id: ID!) {
    location(id: $id) {
      id
      kollaConsumerId
    }
  }
`)

/**
 * When the practice's appointments last came in from the PMS.
 *
 * Hamblin's sync ran at 21:53 the previous evening, so today's board is always
 * a few hours stale and this morning's cancellations are not in it. The week
 * says so rather than letting a stale board read as live.
 */
export const SCHEDULE_LOCATION_QUERY = graphql(`
  query ScheduleWeekLocation($id: ID!) {
    location(id: $id) {
      id
      name
      timezone
      lastAppointmentSyncDate
    }
  }
`)
